// SettingsSequencesPane.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Services
import { updateBase } from 'services/bases';
import {
  buildSequenceSettingsPatch,
  buildSequenceSettingsRows,
  sequenceSettingsRowsEqual,
  validateSequencePatternInput,
} from 'services/bases/sequenceBaseSettings';

// Utilities
import { previewSequencePattern } from 'utilities/SequencePattern';

// CSS
import './SettingsSequencesPane.css';
import './SettingsNamePane.css';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import SettingsModalPaneHeader from './SettingsModalPaneHeader';

/**
 * SettingsSequencesPane
 *
 * Base-owned pattern + next values for Sequence fields (system and custom).
 */
const SettingsSequencesPane = React.memo(({
  theme,
  base = null,
  canEditBase = false,
}) => {
  // ----- CONTEXT ----- //

  const { setActiveBase } = useBase();
  const { getAll, getRevision } = useNodeTypes();
  const nodeTypesRevision = getRevision();

  // ----- MEMOS ----- //

  const typeDefs = useMemo(() => getAll() || [], [getAll, nodeTypesRevision]);

  const persistedRows = useMemo(
    () => buildSequenceSettingsRows(base, typeDefs),
    [base, typeDefs],
  );

  // ----- STATE ----- //

  const [draftRows, setDraftRows] = useState(persistedRows);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setDraftRows(persistedRows);
  }, [persistedRows]);

  const hasChanges = !sequenceSettingsRowsEqual(draftRows, persistedRows);
  const controlsDisabled = !canEditBase || isSaving;

  const validationError = useMemo(() => {
    for (const row of draftRows) {
      const message = validateSequencePatternInput(row.pattern);
      if (message) {
        return `${row.typeLabel} → ${row.fieldLabel}: ${message}`;
      }
    }
    return '';
  }, [draftRows]);

  // ----- HANDLERS ----- //

  const clearStatus = () => {
    setErrorMessage('');
    setStatusMessage('');
  };

  /**
   * Updates one draft row field.
   *
   * @param {string} settingsKey
   * @param {'pattern'|'next'} key
   * @param {string} value
   */
  const handleRowChange = (settingsKey, key, value) => {
    setDraftRows((previous) => previous.map((row) => (
      row.settingsKey === settingsKey
        ? { ...row, [key]: value }
        : row
    )));
    clearStatus();
  };

  /**
   * Saves sequence settings onto the base document.
   */
  const handleSave = async () => {
    if (isSaving || !base?.key || !canEditBase) return;
    if (validationError) {
      setErrorMessage(validationError);
      setStatusMessage('');
      return;
    }

    setErrorMessage('');
    setStatusMessage('Saving...');
    setIsSaving(true);

    const patch = buildSequenceSettingsPatch(draftRows);

    try {
      await updateBase(base.key, patch);
      setActiveBase((previous) => {
        if (!previous || previous.key !== base.key) return previous;
        return {
          ...previous,
          sequences: patch.sequences,
        };
      });
      setStatusMessage('Sequence settings updated.');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to update sequence settings.');
      setStatusMessage('');
    } finally {
      setIsSaving(false);
    }
  };

  // ----- RENDER ----- //

  return (
    <>
      <SettingsModalPaneHeader
        title="Sequences"
        subtitle="Patterns and next numbers for auto-generated business IDs (PO numbers, invoices, and more)."
      />

      <SettingsModalPaneContents>
        {draftRows.length === 0 ? (
          <div
            className="settings-sequences-empty"
            style={{
              color: theme.foregroundMuted,
            }}
          >
            No Sequence fields in this base yet. When a type includes a Sequence
            field, it will appear here so you can set the pattern and starting number.
          </div>
        ) : (
          <div className="settings-sequences-fields">
            {draftRows.map((row) => {
              const preview = previewSequencePattern(row.pattern, row.typeKey, {
                seq: Number(row.next) > 0 ? Number(row.next) : 1,
              });

              return (
                <div
                  key={row.settingsKey}
                  className="settings-sequences-row"
                  style={{
                    borderColor: theme.backgroundHover,
                  }}
                >
                  {/* ROW HEADER */}
                  <div className="settings-sequences-row-header">
                    <div className="settings-sequences-row-title">
                      {row.typeLabel}
                    </div>
                    <div
                      className="settings-sequences-row-subtitle"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                      {row.fieldLabel}
                    </div>
                  </div>

                  {/* PATTERN */}
                  <div className="settings-sequences-field">
                    <div className="settings-modal-field-label">
                      Pattern
                    </div>
                    <TextInput
                      value={row.pattern}
                      disabled={controlsDisabled}
                      maxLength={80}
                      width="100%"
                      placeholder="PO-{{seq}}"
                      onChange={(event) => handleRowChange(
                        row.settingsKey,
                        'pattern',
                        event?.target?.value || '',
                      )}
                      onEnterPress={hasChanges && !validationError ? handleSave : undefined}
                    />
                    <div
                      className="settings-sequences-hint"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                      Tokens: {'{{seq}}'}, {'{{seq:5}}'}, {'{{year}}'}, {'{{type}}'}
                    </div>
                  </div>

                  {/* NEXT */}
                  <div className="settings-sequences-field">
                    <div className="settings-modal-field-label">
                      Next number
                    </div>
                    <TextInput
                      value={String(row.next ?? 1)}
                      disabled={controlsDisabled}
                      maxLength={12}
                      width="100%"
                      placeholder="1"
                      onChange={(event) => handleRowChange(
                        row.settingsKey,
                        'next',
                        event?.target?.value || '',
                      )}
                      onEnterPress={hasChanges && !validationError ? handleSave : undefined}
                    />
                  </div>

                  {/* PREVIEW */}
                  <div
                    className="settings-sequences-preview"
                    style={{
                      color: theme.foregroundMuted,
                    }}
                  >
                    Next value preview:
                    {' '}
                    <span
                      style={{
                        color: theme.foreground,
                      }}
                    >
                      {preview || '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(errorMessage || statusMessage) ? (
          <div
            className="settings-sequences-status"
            style={{
              color: errorMessage ? theme.warning : theme.foregroundMuted,
            }}
          >
            {errorMessage || statusMessage}
          </div>
        ) : null}

        {canEditBase && draftRows.length > 0 ? (
          <div className="settings-sequences-actions">
            <Button
              iconKey="save"
              label={isSaving ? 'Saving…' : 'Save'}
              enabled={hasChanges && !validationError && !isSaving}
              onClick={handleSave}
            />
          </div>
        ) : null}
      </SettingsModalPaneContents>
    </>
  );
});

// Dev Tools
SettingsSequencesPane.displayName = 'SettingsSequencesPane';

export default SettingsSequencesPane;
